import { normalizeBlogCategory, orderBlogCategories } from "@/lib/content-metadata"
import type { ContentEntry } from "@/lib/content-resolver"

export interface BlogSearch {
  category?: string
}

export function getPostTitle(post: ContentEntry) {
  return String(post.frontmatter["title"] ?? "")
}

export function getPostDescription(post: ContentEntry) {
  return String(post.frontmatter["description"] ?? "")
}

export function getPostCategory(post: ContentEntry) {
  return String(post.frontmatter["category"] ?? "")
}

function getPostAuthor(post: ContentEntry) {
  const author = post.frontmatter["author"]

  return typeof author === "string" ? author : undefined
}

export function getPostFeatureImage(post: ContentEntry) {
  const image = post.frontmatter["featureImage"]

  return typeof image === "string" ? image : undefined
}

export function getPostFeatureImageWebp(post: ContentEntry) {
  const image = post.frontmatter["featureImageWebp"]

  return typeof image === "string" ? image : undefined
}

export function getPostFeatureImageAlt(post: ContentEntry) {
  const alt = post.frontmatter["featureImageAlt"]

  return typeof alt === "string" ? alt : ""
}

/** The post's thumbnail stays in listings, but the article page doesn't lead with it. */
export function isFeatureImageHidden(post: ContentEntry) {
  const hidden = post.frontmatter["hideFeatureImage"]

  return hidden === true || hidden === "true"
}

function isFeaturedPost(post: ContentEntry) {
  const featured = post.frontmatter["featured"]

  return featured === true || featured === "true"
}

export function getBlogMeta(post: ContentEntry) {
  const author = getPostAuthor(post)
  const date = formatBlogMetaDate(post.date)

  return author ? `${date} · ${author}` : date
}

export function formatBlogMetaDate(date: string) {
  const [year, month, day] = date.split("-")
  const parsedDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))

  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  })
    .format(parsedDate)
    .toUpperCase()
}

export function getBlogCategories(posts: ContentEntry[]) {
  return orderBlogCategories(posts.map(getPostCategory))
}

export function getValidCategory(posts: ContentEntry[], category?: string) {
  if (!category) {
    return undefined
  }

  const normalizedCategory = normalizeBlogCategory(category)

  return getBlogCategories(posts).includes(normalizedCategory) ? normalizedCategory : undefined
}

export function filterBlogPosts(posts: ContentEntry[], category?: string) {
  const validCategory = getValidCategory(posts, category)

  if (!validCategory) {
    return posts
  }

  return posts.filter((post) => getPostCategory(post) === validCategory)
}

export function getLatestPost(posts: ContentEntry[]) {
  return posts[0] ?? null
}

export function getFeaturedPost(posts: ContentEntry[]) {
  return posts.find(isFeaturedPost) ?? getLatestPost(posts)
}

export function getPostStrip(posts: ContentEntry[], excludedSlug?: string) {
  return posts.filter((post) => post.slug !== excludedSlug).slice(0, 3)
}
